import { Track } from "@/src/lib/tracking";
import { CHAT_EVENTS } from "@/src/components/Chat/chatTrackingEvents";
import { ChatStore } from "@store/modules/chat";
import { resolveAllowedChatMode } from "@utils/chatMode";
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  useMemo,
  useImperativeHandle,
} from "react";
import { useParams } from "next/navigation";
import {
  deviceDetector,
  Icon,
  ospThemes,
  TextAreaField,
  type TextAreaFieldInstance,
} from "@ospweb/sdk-ui";
import { cn, getAssetsUrl } from "utils";
import type { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";
import { useSnapshot } from "valtio";
import { useMemoizedFn } from "ahooks";
import { OspIcons } from "@ospweb/sdk-ui/src/Icon/OspIcons";
import { ShowOnPcOrMobile } from "../ShowOnPcOrMobile";
import { QuestionItem, type Question } from "./QuestionItem";
import { ChatModeSelector } from "./ChatModeSelector";
import { SenderAttachment, SenderProps, SenderSubmitOptions } from "./type";
import { useSenderComposition } from "./sender.composition";
import { useSenderEnterSubmit } from "./sender.enterSubmit";
import { useSenderPendingSendWhileUpload } from "./sender.pendingSendWhileUpload";
import { useSenderFileAttachments } from "./sender.fileAttachments";
import {
  SenderAttachmentSourceMenu,
  type SenderAttachmentConnectMenu,
} from "./SenderAttachmentSourceMenu";
import { SenderFileDropOverlay } from "./SenderFileDropOverlay";
import { useSenderFileDropInteraction } from "./useSenderFileDropInteraction";
import {
  useCurrentMessageVoiceState,
  type VoiceRecognitionResult,
} from "./useCurrentMessageVoiceState";
import {
  useVoiceRecorder,
  isDeepSearchVoiceAttachmentEnabled,
} from "./useVoiceRecorder";
import { VoiceRecorder } from "./VoiceRecorder";
import { SenderAttachmentChip } from "./SenderAttachmentChip";
import { useHasVoiceAccess } from "./useHasVoiceAccess";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Sender/Sender";
import { rootStore } from "@store/rootStore";
import { useSenderMessage } from "@/src/hooks/useSenderMessage";
import { useRequestHeader } from "@/src/hooks/useRequestHeader";
import { LOCAL_STORAGE_KEYS } from "@config/constant";
import { chatClientApiBaseUrl } from "@config/config";
import { hasFileUploadAccess as resolveFileUploadAccess } from "@/src/lib/bridge/filePermissions";
import { useAppFeatures } from "@/src/lib/bridge/useBridge";
import { buildOpenReplayLabel } from "@utils/openreplay";
import { formatSecondOpinionOutboundMessage } from "./sender.secondOpinion";

/**
 * Sender 组件的 ref 接口
 */
export interface SenderRef {
  /**
   * 设置值并聚焦到输入框
   * @param value 要设置的值
   * @param focus 是否聚焦到输入框，默认为 true
   */
  requestFocus: (focus?: boolean) => void;
}

const I18n = I18nUtil.createScoped(Translations);
const Sender = forwardRef<SenderRef, SenderProps>((props, ref) => {
  const {
    onSend,
    style,
    className,
    containerClassName,
    value,
    onChange,
    disabled = false,
    loading = false,
    placeholder,
    placeholderColor,
    hideDeepSearch = false,
    onStop,
    questions,
    onQuestionSelect,
    chatRefContent,
    onCloseChatToast,
    onChatClick,
    onFocus,
    readOnly = false,
    withQuestions = true,
    expandClassName,
    textareaStyles,
    // 切换思考模式后，移除后面的问题推荐
    senderQuestion: _senderQuestion,
    openReplaySurface,
    defaultSecondOpinionActive = false,
    connectMenu,
  } = props;
  const senderRef = useRef<HTMLDivElement>(null);
  const clearFileDragStateRef = useRef<() => void>(() => {});
  const textAreaRef = useRef<TextAreaFieldInstance>(null);
  const [isFocus, setIsFocus] = useState(false);
  const userInputRef = useRef(value || "");
  const attachmentsScrollRef = useRef<HTMLDivElement>(null);
  const [displayValue, setDisplayValue] = useState(value || "");
  const [isSecondOpinionActive, setIsSecondOpinionActive] = useState(
    defaultSecondOpinionActive,
  );

  useEffect(() => {
    setIsSecondOpinionActive(defaultSecondOpinionActive);
  }, [defaultSecondOpinionActive]);
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { fingerprint } = useSnapshot(rootStore.user);
  const { headers: authHeaders } = useRequestHeader();
  const { device } = useParams<{ device: DeviceType }>();
  const isH5 = device !== "desktop";
  /** Native App：专用 photo/camera/document input + bridge prepareFileInput */
  const useAttachmentAppPickers = deviceDetector.isApp();
  const appFeatures = useAppFeatures();
  const hasFileUploadAccess =
    !deviceDetector.isApp() || resolveFileUploadAccess(appFeatures);
  const showVoiceButton = useHasVoiceAccess();

  const setDisplayValueFunc = useMemoizedFn((value: string) => {
    setDisplayValue(value);
    userInputRef.current = value;
  });

  // 暴露给父组件的方法
  useImperativeHandle(
    ref,
    () => ({
      requestFocus: () => {
        // 聚焦到输入框
        textAreaRef.current?.resizableTextArea?.textArea?.focus();
      },
    }),
    [],
  );

  useEffect(() => {
    if (!questions?.length) {
      return;
    }
    try {
      Track.log("info", "inputchat", "recommend questions", {
        cause: String(JSON.stringify(questions)),
      });
    } catch {
      Track.log("info", "inputchat", "recommend questions error", {
        cause: String(questions),
      });
    }
  }, [questions]);

  // 推荐问题功能：只有在传入 questions 时才启用
  const hasRecommendQuestions = questions && questions.length > 0;

  const showChatRefToast = !!chatRefContent && !!chatRefContent?.refId;

  // 处理推荐问题选择（回填到输入框）
  const handleQuestionSelect = useCallback(
    (question: Question) => {
      if (!hasRecommendQuestions) {
        return;
      }
      setDisplayValueFunc(question.question);
      // 触发 onChange 回调
      if (onChange) {
        const syntheticEvent = {
          target: { value: question.question },
        } as any;
        onChange(syntheticEvent);
      }
      // 聚焦到输入框
      const textArea = textAreaRef.current?.resizableTextArea?.textArea;
      if (textArea) {
        textArea.focus();
      }
      onQuestionSelect?.(question);
    },
    [hasRecommendQuestions, onChange, onQuestionSelect, setDisplayValueFunc],
  );

  // Update displayValue when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setDisplayValueFunc(value);
    }
  }, [value, setDisplayValueFunc]);

  // 当 chatRefContent 变化且有 question 时，自动聚焦输入框
  useEffect(() => {
    if (chatRefContent?.question && chatRefContent?.refId) {
      // 延迟一下确保 value 已经更新
      const timer = setTimeout(() => {
        const textArea = textAreaRef.current?.resizableTextArea?.textArea;
        if (textArea) {
          textArea.focus();
          setIsFocus(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [chatRefContent]);

  // 获取输入值的函数 - now returns the ref value to avoid unnecessary re-renders
  const getInputValue = useCallback(() => {
    // First check the ref for the most up-to-date value
    if (userInputRef.current) {
      return userInputRef.current.trim();
    }
    // Fallback to directly reading from textarea
    const textArea = textAreaRef.current?.resizableTextArea?.textArea;
    const textAreaValue = textArea?.value?.trim() || "";
    if (textAreaValue) {
      userInputRef.current = textAreaValue; // Update ref with textarea value
    }
    return textAreaValue;
  }, []);

  const onBlurSender = useCallback(() => {
    setIsFocus(false);
    textAreaRef.current?.blur();
  }, []);

  // 清空输入框并聚焦
  const clearAndFocusInput = useCallback(() => {
    setDisplayValueFunc("");
    onBlurSender();
  }, [onBlurSender, setDisplayValueFunc]);

  const {
    attachments,
    readyAttachments,
    isUploading,
    fileInputRef,
    photoInputRef,
    cameraInputRef,
    documentInputRef,
    clearAttachments,
    handlePickFiles,
    handlePickPhoto,
    handlePickCamera,
    handlePickDocuments,
    handleRemoveAttachment,
    handleFilesSelected,
    handleFileChange,
    handlePhotoChange,
    handleCameraChange,
    handleDocumentChange,
    handleDropToUpload,
    handlePaste,
  } = useSenderFileAttachments({
    disabled,
    loading,
    isLoggedIn: !!isLoggedIn,
    hasFileUploadAccess: !!hasFileUploadAccess,
    authHeaders,
    fingerprint,
    onDragStateClear: () => clearFileDragStateRef.current(),
  });
  const { appendTurn, buildVoiceContext, resetVoiceState } =
    useCurrentMessageVoiceState();

  // Use the new hook
  const { handleSend: handleSendMessage } = useSenderMessage({
    onSend,
    clearAndFocusInput,
    clearAttachments,
    clearVoiceContext: resetVoiceState,
    hideDeepSearch,
    disabled,
    loading,
  });

  const uploadVoiceAsAttachment = useMemoizedFn((audioBlob: Blob) => {
    const file = new File([audioBlob], `voice-${Date.now()}.wav`, {
      type: "audio/wav",
    });
    void handleFilesSelected([file]);
  });

  const uploadVoiceBlobForSend = useMemoizedFn(
    async (audioBlob: Blob): Promise<SenderAttachment | null> => {
      try {
        const file = new File([audioBlob], `voice-${Date.now()}.wav`, {
          type: "audio/wav",
        });
        const formData = new FormData();
        formData.append("files", file);
        const uploadApiBaseUrl = (() => {
          const env = process.env
            .NEXT_PUBLIC_ENV as keyof typeof chatClientApiBaseUrl;
          return chatClientApiBaseUrl[env] || chatClientApiBaseUrl.prod;
        })();
        const res = await fetch(`${uploadApiBaseUrl}/attachments/upload`, {
          method: "POST",
          headers: {
            ...authHeaders,
            ...(fingerprint ? { guestId: String(fingerprint) } : {}),
          },
          body: formData,
        });
        if (!res.ok) {
          return null;
        }
        const json = await res.json();
        if (!json.success || !Array.isArray(json.data) || !json.data[0]) {
          return null;
        }
        const item = json.data[0];
        return {
          type: "file",
          filename: item.filename,
          mediaType: item.mediaType || "audio/wav",
          url: item.url,
          size: item.size,
        };
      } catch (err) {
        Track.log("warn", "voice", "upload voice attachment failed", {
          cause: String(err),
        });
        return null;
      }
    },
  );

  const handleVoiceTranscribeComplete = useMemoizedFn(
    (result: VoiceRecognitionResult) => {
      appendTurn(result);
      if (isDeepSearchVoiceAttachmentEnabled() && result.audioBlob) {
        uploadVoiceAsAttachment(result.audioBlob);
      }
      const nextValue = [displayValue.trim(), result.text]
        .filter(Boolean)
        .join(" ");
      setDisplayValueFunc(nextValue);
      onChange?.({
        target: { value: nextValue },
      });
    },
  );

  const handleVoiceDirectSend = useMemoizedFn(
    async (result: VoiceRecognitionResult) => {
      appendTurn(result);
      const nextValue = [displayValue.trim(), result.text]
        .filter(Boolean)
        .join(" ");
      const voiceContext = buildVoiceContext();

      let voiceAttachments: SenderAttachment[] = [];
      if (isDeepSearchVoiceAttachmentEnabled() && result.audioBlob) {
        const uploaded = await uploadVoiceBlobForSend(result.audioBlob);
        if (uploaded) {
          voiceAttachments = [uploaded];
        }
      }

      const options: SenderSubmitOptions | undefined =
        voiceContext || voiceAttachments.length > 0
          ? {
              ...(voiceContext ? { voiceContext } : {}),
              ...(voiceAttachments.length > 0
                ? { attachments: voiceAttachments, forceAttachments: true }
                : {}),
            }
          : undefined;
      try {
        const outboundMessage = formatSecondOpinionOutboundMessage(
          nextValue,
          isSecondOpinionActive,
        );
        const sendResult = await Promise.resolve(
          onSend(outboundMessage, {
            ...options,
            ...(isSecondOpinionActive ? { secondOpinion: true } : {}),
          }),
        );
        handleClearSecondOpinion();
        const resolved =
          sendResult && typeof sendResult === "object" && "sent" in sendResult
            ? sendResult
            : undefined;

        if (resolved ? resolved.sent : true) {
          clearAndFocusInput();
          resetVoiceState();
        }
      } catch {
        resetVoiceState();
      }
    },
  );

  const {
    voicePhase,
    analyserNode,
    startRecording,
    stopRecording,
    sendRecording,
    cancelTranscribe,
  } = useVoiceRecorder({
    disabled,
    loading,
    authHeaders,
    fingerprint,
    onTranscribeComplete: handleVoiceTranscribeComplete,
    onDirectSendComplete: handleVoiceDirectSend,
  });

  const isVoiceActive =
    voicePhase === "recording" || voicePhase === "transcribing";

  const canShowFileDropUi = !deviceDetector.isApp() && !isVoiceActive;

  const { fileDragPhase, clearFileDragState, dropZoneDragHandlers } =
    useSenderFileDropInteraction({
      enabled: canShowFileDropUi,
      dropZoneRef: senderRef,
      onDropToUpload: handleDropToUpload,
    });

  clearFileDragStateRef.current = clearFileDragState;

  // 计算是否显示推荐问题（聚焦且输入为空）
  const shouldShowRecommendations = useMemo(
    () =>
      withQuestions &&
      hasRecommendQuestions &&
      isFocus &&
      displayValue.trim() === "" &&
      !showChatRefToast &&
      !isVoiceActive,
    [
      hasRecommendQuestions,
      isFocus,
      displayValue,
      showChatRefToast,
      withQuestions,
      isVoiceActive,
    ],
  );

  // 是否展示 expand
  const showExpand = shouldShowRecommendations || showChatRefToast;

  const { isComposingRef, onCompositionStart, onCompositionEnd } =
    useSenderComposition();

  useEffect(() => {
    const handleOutsideMousedown = (event: MouseEvent) => {
      if (
        senderRef.current &&
        !senderRef.current.contains(event.target as Node)
      ) {
        onBlurSender();
      }
    };

    if (!disabled && isFocus) {
      document.addEventListener("mousedown", handleOutsideMousedown);
      return () => {
        document.removeEventListener("mousedown", handleOutsideMousedown);
      };
    }
  }, [disabled, isFocus, onBlurSender]);

  const executeSend = useMemoizedFn(() => {
    const inputValue = getInputValue();
    const nextAttachments = readyAttachments;
    // 统一发送规则：必须有文字输入（trim 后非空）；附件仅作为随文字一起发送的附加信息
    if (!inputValue) {
      return;
    }
    const voiceContext = buildVoiceContext();
    const outboundMessage = formatSecondOpinionOutboundMessage(
      inputValue,
      isSecondOpinionActive,
    );
    handleClearSecondOpinion();
    /**
     * 「发出一条消息」的**唯一出口** —— 按钮点击 / 回车 / 上传完成后补发三种触发都汇到
     * `executeSend`，所以埋点只放这一处（放调用方就会漏其中一种）。
     *
     * 改之前这个动作**只有点击 label、没有 payload**：点击消息在协议层就没有 payload 字段，
     * 所以「用哪种方式发的」（语音 / 带附件 / second opinion）一个都拿不到。
     * 下游 `edgen_events` 用它算互动计数与频次预算，产品侧要看语音占比，都需要这些维度。
     *
     * ⚠️ **本条不带 `chat_id`**：Sender 是纯输入组件，拿不到权威的 chat id
     * （`useChats` 是 `resolveChatId(props ?? localStorage, params)` 推出来的；
     * `ChatStore.sessionId` 只在 ChatModal / History 两处赋值，主 chat 页是空的）。
     * 在这里重新推一遍会出现两个来源不一致 —— **比没有这个字段更糟**。
     * chat id 挂在同一轮的 `chat_response_done` 上（那边有权威值），
     * 一轮内的配对靠 envelope 的 `app_run_id` + `seq` 顺序。
     */
    Track.event(CHAT_EVENTS.messageSent, {
      has_attachment: nextAttachments.length > 0,
      attachment_count: nextAttachments.length,
      is_voice: Boolean(voiceContext),
      second_opinion: isSecondOpinionActive,
      // `chat_mode`：Fast(`qa`) / Think(`agent-performance`)。native 一直带这个字段，
      // web 此前没有 —— 下游按它分会话类型时会得到「只有 app 用户有模式」的偏斜数据。
      // 读裸 store 而不是 `useSnapshot`：这里是事件回调，要的是**发送那一刻**的值，
      // 且不该让模式切换触发 Sender 重渲染。归一化与 `useChats` 发请求时共用同一个函数，
      // 否则会出现「上报的模式 ≠ 真正请求的模式」。
      chat_mode: resolveAllowedChatMode(ChatStore.chatMode),
    });
    handleSendMessage(outboundMessage, {
      attachments: nextAttachments,
      ...(voiceContext ? { voiceContext } : {}),
      ...(isSecondOpinionActive ? { secondOpinion: true } : {}),
    });
  });

  const { trySend } = useSenderPendingSendWhileUpload({
    isUploading,
    flushSend: executeSend,
  });

  const { onKeyDown: handleKeyDown } = useSenderEnterSubmit({
    isComposingRef,
    shouldAllowEnterSubmit: !readOnly,
    onEnter: (e) => {
      const textArea = e.currentTarget;
      if (textArea && textArea.value) {
        userInputRef.current = textArea.value;
      }
      trySend();
    },
  });

  const inItPlaceholder = I18n.t(
    "Ask about tokens, trends, people, or profitable moves (and remember to touch grass)",
  );
  const secondOpinionPlaceholder = I18n.t(
    "Drop the plan or a screenshot, I'll help you read it and spot the catches",
  );
  const composerPlaceholder = isSecondOpinionActive
    ? secondOpinionPlaceholder
    : placeholder || inItPlaceholder;

  const handleActivateSecondOpinion = useMemoizedFn(() => {
    setIsSecondOpinionActive(true);
  });

  const handleClearSecondOpinion = useMemoizedFn(() => {
    setIsSecondOpinionActive(false);
  });

  // EDG-591：`+` 菜单顶部上传 / 连接项（Figma 110225:595520）
  const attachmentConnectMenu = useMemo<
    SenderAttachmentConnectMenu | undefined
  >(() => {
    if (!connectMenu) {
      return undefined;
    }
    const { hasAccountData, hasPlaidLink, trackingParams } = connectMenu;
    return {
      uploadLabel:
        hasAccountData === false
          ? I18n.t("Upload holdings screenshot")
          : I18n.t("Upload a statement"),
      connectLabel:
        hasPlaidLink === false ? I18n.t("Connect an account") : undefined,
      hasAccountData: trackingParams.has_account_data,
      hasPlaidLink: trackingParams.has_plaid_link,
      onUpload: connectMenu.onUpload,
      onConnect: connectMenu.onConnect,
      onDismiss: () =>
        Track.event("connect_entry_dismiss", { surface: "composer_menu" }),
    };
  }, [connectMenu]);

  const handleSend = useMemoizedFn(() => {
    Track.log("info", "useChats", "stop params: handleSend", {
      cause: String(loading),
    });
    if (loading && onStop) {
      onStop?.();
      return;
    }
    trySend();
  });

  const enableButton =
    (loading &&
      onStop &&
      !localStorage.getItem(LOCAL_STORAGE_KEYS.HIDDEN_CHAT_ID)) ||
    isFocus;
  const isSendingStoppable = !!(
    loading &&
    onStop &&
    !localStorage.getItem(LOCAL_STORAGE_KEYS.HIDDEN_CHAT_ID)
  );

  useEffect(() => {
    const el = attachmentsScrollRef.current;
    if (!el || attachments.length === 0) {
      return;
    }
    requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth;
    });
  }, [attachments.length]);

  useEffect(() => {
    if (disabled || loading) {
      clearFileDragState();
    }
  }, [disabled, loading, clearFileDragState]);

  const curshowChatEntry =
    typeof onChatClick === "function" &&
    !showChatRefToast &&
    !shouldShowRecommendations;

  const OR_SCENE = "ChatBox";

  const sendButtonClassName = useMemo(() => {
    return cn(
      "w-9 h-9 rounded-full flex items-center justify-center shrink-0",
      enableButton
        ? "bg-background_white_overlay100"
        : "bg-background_white_overlay20",
    );
  }, [enableButton]);

  return (
    <div
      className={cn("relative", containerClassName)}
      data-tracking-scope="sender"
    >
      {curshowChatEntry && (
        <ShowOnPcOrMobile showMode="mobile" hideOnMobileApp>
          <div
            data-openreplay-label={buildOpenReplayLabel(
              OR_SCENE,
              "PageEntry",
              "Click",
            )}
            className="flex absolute -top-[24px] justify-center bg-cover bg-center bg-no-repeat h-[38px] w-full backdrop-blur-[12px]"
            onClick={onChatClick}
            style={{
              backgroundImage: `url(${getAssetsUrl("/assets/images/chat_btn_h5_bg.webp")})`,
            }}
          >
            <OspIcons
              name="first-page"
              size={16}
              color={ospThemes.colors.content_brand_primary}
              className="rotate-90 w-4 h-4 ml-[6px]"
            />
          </div>
        </ShowOnPcOrMobile>
      )}
      <div
        data-openreplay-label={
          readOnly && onChatClick
            ? buildOpenReplayLabel(OR_SCENE, "Composer", "Open")
            : undefined
        }
        ref={senderRef}
        {...dropZoneDragHandlers}
        style={
          isH5
            ? {
                ...style,
                outline: "1px var(--edgen-border_white_transparent5) solid",
                outlineOffset: "-1px",
              }
            : style
        }
        className={cn([
          // 根据是否有推荐问题使用不同的样式
          isH5
            ? "w-full py-3 rounded-4 flex flex-col gap-3 bg-background_solid_neutral_primary shadow-[0px_0px_32px_rgba(0,0,0,0.08)] overflow-x-hidden"
            : showExpand
              ? "rounded-3 relative overflow-hidden border border-solid transition-colors duration-0"
              : `rounded-3 relative overflow-hidden ${expandClassName || "py-3"}`,
          !isH5 && showExpand && " border-border_white_transparent10",
          // (isFocus
          //   ? "border-border_brand_solid"
          //   : " border-border_brand_transparent50 hover:border-border_brand_solid"),
          className,
          !isH5 &&
            "w-full group bg-background_solid_neutral_primary pb-4 md:shadow-[0_0_80px_0_rgb(0,0,0,0.08)]",
          fileDragPhase !== "idle" && "min-h-[180px]",
          // readOnly 模式下添加点击样式和事件
          readOnly && onChatClick && "cursor-pointer",
          {
            "p-0": isVoiceActive,
          },
        ])}
        id="container"
        onClick={readOnly && onChatClick ? onChatClick : undefined}
      >
        <SenderFileDropOverlay phase={fileDragPhase} />
        {/* 原有的边框样式（当没有推荐问题时使用） */}
        {!isH5 && !showExpand && (
          <div
            className={cn([
              "rounded-3 border-1",
              "absolute inset-0",
              "border-border_white_transparent10",
            ])}
          ></div>
        )}

        {/* 推荐问题列表（在输入框上方，同一边框内） */}
        {showExpand && (
          <div
            className={cn(
              "transition-all duration-0 ease-in-out px-4 md:px-0",
              "overflow-hidden",
              showExpand
                ? "max-h-96 opacity-100 "
                : "max-h-0 opacity-0 border-b-0",
              shouldShowRecommendations
                ? "border-b border-solid border-border_solid"
                : "",
            )}
          >
            {shouldShowRecommendations && !showChatRefToast && (
              <div className="flex flex-col p-2">
                {questions &&
                  questions.map((question) => (
                    <QuestionItem
                      key={question.id}
                      openReplayLabel={buildOpenReplayLabel(
                        OR_SCENE,
                        "QuestionSuggestion",
                        "Click",
                      )}
                      question={question}
                      onClick={handleQuestionSelect}
                    />
                  ))}
              </div>
            )}
            {showChatRefToast && (
              <div className="flex flex-row gap-1 justify-between p-3">
                <div className="flex flex-row items-center gap-2">
                  <div className="w-[2px] self-stretch bg-background_white_overlay20"></div>
                  <span className="text-content_tertiary body_m line-clamp-2">
                    {chatRefContent?.title}
                  </span>
                </div>
                <Icon
                  data-openreplay-label={buildOpenReplayLabel(
                    OR_SCENE,
                    "ReferenceMessage",
                    "Close",
                  )}
                  name="close-circle"
                  size={20}
                  className="text-content_primary cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onCloseChatToast?.();
                  }}
                />
              </div>
            )}
          </div>
        )}

        {(attachments.length > 0 || isSecondOpinionActive) && (
          <div
            ref={attachmentsScrollRef}
            className={cn(
              "flex gap-2 px-4 overflow-x-auto md:pb-4 z-5 relative no-scrollbar",
              {
                "pt-3":
                  showExpand &&
                  shouldShowRecommendations &&
                  (!!questions?.length || showChatRefToast),
              },
            )}
          >
            {isSecondOpinionActive ? (
              <div
                className="flex w-fit h-[44px] p-2 max-w-full items-center gap-1 rounded-xl bg-background_brand_overlay10"
                data-testid="sender-second-opinion-chip"
              >
                <Icon
                  name="ed"
                  size={16}
                  className="shrink-0 text-content_brand_primary"
                  aria-hidden
                />
                <span className="body_s text-content_brand_primary whitespace-nowrap">
                  {I18n.t("Second opinion")}
                </span>
                <button
                  type="button"
                  className="flex shrink-0 items-center justify-center"
                  aria-label="Clear Second opinion"
                  data-openreplay-label={buildOpenReplayLabel(
                    OR_SCENE,
                    "SecondOpinion",
                    "Clear",
                  )}
                  onClick={handleClearSecondOpinion}
                >
                  <Icon
                    name="close"
                    className="shrink-0 text-content_primary bg-background_white_overlay10 rounded-full p-0.5"
                    size={16}
                  />
                </button>
              </div>
            ) : null}
            {attachments.map((attachment, index) => (
              <SenderAttachmentChip
                key={`${attachment.filename}-${index}`}
                attachment={attachment}
                index={index}
                onRemove={handleRemoveAttachment}
              />
            ))}
          </div>
        )}
        <div
          className={cn("relative z-[5] flex flex-col w-full px-4 md:px-0", {
            "pt-3": showExpand && !isH5 && !attachments?.length,
          })}
        >
          {isVoiceActive ? (
            <VoiceRecorder
              phase={voicePhase as "recording" | "transcribing"}
              onStop={stopRecording}
              onDirectSend={sendRecording}
              onCancelTranscribe={cancelTranscribe}
              analyserNode={analyserNode}
            />
          ) : (
            <div
              data-openreplay-label={buildOpenReplayLabel(
                OR_SCENE,
                "MessageInput",
                "DropZone",
              )}
              className="rounded-3 transition-colors"
              onPaste={(e) => {
                void handlePaste(e);
              }}
            >
              <TextAreaField
                ref={textAreaRef}
                value={displayValue}
                data-openreplay-label={buildOpenReplayLabel(
                  OR_SCENE,
                  "MessageInput",
                  "Edit",
                )}
                borderVisible={false}
                onKeyDown={handleKeyDown}
                readOnly={readOnly}
                placeholderClass={isH5 ? "left-0" : ""}
                containerClassName={
                  isH5 ? "w-full bg-transparent min-h-[20px] px-0" : ""
                }
                onCompositionStart={onCompositionStart}
                onCompositionEnd={onCompositionEnd}
                onFocus={() => {
                  setIsFocus(true);
                  onFocus?.();
                }}
                placeholder={composerPlaceholder}
                placeholderColor={placeholderColor}
                autoSize={
                  textareaStyles?.height ? false : { minRows: 1, maxRows: 6 }
                }
                autoFocus={readOnly ? false : undefined}
                size="small"
                styles={
                  textareaStyles
                    ? {
                        textarea: textareaStyles,
                      }
                    : undefined
                }
                onChange={(e: any) => {
                  // Update userInput ref when the textarea changes
                  if (e?.target?.value !== undefined) {
                    setDisplayValueFunc(e.target.value);
                  }
                  // Call the original onChange handler if provided
                  if (onChange) {
                    onChange(e);
                  }
                }}
              />
            </div>
          )}
          {isVoiceActive ? null : (
            <div className="flex items-center justify-between w-full mt-3 md:px-3">
              {hasFileUploadAccess ? (
                <>
                  {useAttachmentAppPickers ? (
                    <>
                      <input
                        ref={photoInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        className="hidden"
                        onChange={handlePhotoChange}
                      />
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={handleCameraChange}
                      />
                      <input
                        ref={documentInputRef}
                        type="file"
                        multiple
                        accept="*/*"
                        className="hidden"
                        onChange={handleDocumentChange}
                      />
                    </>
                  ) : (
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="*/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  )}
                  <SenderAttachmentSourceMenu
                    disabled={disabled || loading}
                    connectMenu={attachmentConnectMenu}
                    variant={useAttachmentAppPickers ? "app" : "web"}
                    secondOpinionDisabled={isSecondOpinionActive}
                    onPickPhoto={handlePickPhoto}
                    onPickCamera={handlePickCamera}
                    onPickFiles={
                      useAttachmentAppPickers
                        ? handlePickDocuments
                        : handlePickFiles
                    }
                    onPickSecondOpinion={handleActivateSecondOpinion}
                  >
                    <button
                      type="button"
                      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                      aria-label="Attach files"
                      data-openreplay-label={buildOpenReplayLabel(
                        OR_SCENE,
                        "Attachment",
                        "Open",
                      )}
                      disabled={disabled || loading}
                    >
                      <Icon
                        name="plus"
                        size={20}
                        className="text-content_primary"
                      />
                    </button>
                  </SenderAttachmentSourceMenu>
                </>
              ) : (
                <Icon
                  name="plus"
                  size={20}
                  className="text-content_quaternary shrink-0"
                />
              )}

              <div className="flex items-center gap-4">
                <ChatModeSelector
                  openReplaySurface={openReplaySurface}
                  disabled={disabled || loading}
                />

                {showVoiceButton && (
                  <button
                    type="button"
                    onClick={startRecording}
                    className="w-9 h-9 rounded-full border border-border_white_transparent20 flex items-center justify-center shrink-0"
                    aria-label="Voice input"
                    data-openreplay-label={buildOpenReplayLabel(
                      OR_SCENE,
                      "VoiceInput",
                      "Open",
                    )}
                    disabled={disabled || loading}
                  >
                    <OspIcons
                      name="microphone"
                      size={20}
                      color={ospThemes.colors.content_primary}
                    />
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleSend}
                  // In RN WebView / iframe, tapping the send button causes the textarea to blur,
                  // which triggers keyboard dismissal and layout reflow. By the time the reflow
                  // settles, the button may have shifted and the `click` event is swallowed.
                  // Fix: handle send on `touchend` (fires before blur side-effects propagate)
                  // and call preventDefault to suppress the redundant `click` that would follow.
                  // The `onClick` below remains as fallback for mouse / keyboard interactions.
                  onTouchEnd={(event) => {
                    if ((event.currentTarget as HTMLButtonElement).disabled) {
                      return;
                    }
                    Track.log("info", "useChats", "stop params: onTouchEnd");
                    // preventDefault stops the browser generating a `click` after touchend,
                    // preventing double-send when the button survives the layout reflow.
                    event.preventDefault();
                    // Explicitly blur the textarea so the keyboard dismisses —
                    // preventDefault above suppresses the natural blur-on-tap side-effect.
                    (document.activeElement as HTMLElement | null)?.blur();
                    handleSend();
                  }}
                  className={sendButtonClassName}
                  aria-label={isSendingStoppable ? "Stop" : "Send"}
                  data-openreplay-label={
                    isSendingStoppable
                      ? buildOpenReplayLabel(OR_SCENE, "MessageInput", "Stop")
                      : buildOpenReplayLabel(
                          OR_SCENE,
                          "MessageInput",
                          // 带不带附件是两种发送行为 —— 旧格式靠第四段文案区分，
                          // 去掉第四段后必须挪进 action 段，否则两者撞成同一个 label
                          readyAttachments.length > 0
                            ? "SubmitWithAttachment"
                            : "Submit",
                        )
                  }
                  disabled={!enableButton}
                >
                  {isSendingStoppable ? (
                    <span
                      className="pointer-events-none w-[8.33px] h-[8.33px] bg-content_inverse_primary shrink-0"
                      aria-hidden
                    />
                  ) : (
                    <Icon
                      name="arrow-right"
                      size={20}
                      color={
                        enableButton
                          ? ospThemes.colors.content_inverse_primary
                          : ospThemes.colors.content_inverse_secondary
                      }
                      className="-rotate-90"
                    />
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

Sender.displayName = "Sender";

export default Sender;
